import { BFScreen } from "@/components/standard/screen/BFScreen"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { BFBackButton } from "@/components/standard/BFBackButton"
import { H5, H6, Small } from "@/components/standard/BFText"
import { type ReactNode, useState } from "react"
import { Pressable, ScrollView, View } from "react-native"
import ArchiveIcon from "@/assets/icons/archive-icon.svg"
import { BFColors } from "@/constants/Colors"
import {
  BFButton,
  type CommonButtonProps,
} from "@/components/buttons/CommonButtons"
import { SetupHeader } from "@/screens/loans/components/setup/SetupHeader"
import { router } from "expo-router"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { LoanArchivePopover } from "@/screens/loans/components/setup/LoanArchivePopover"
import { useArchiveLoanMutation } from "@/queries/loan-dashboard"
import { hideToast, showSuccessToast } from "@/components/toasts/AppToast"
import { useLoanContext } from "@/screens/loans/components/LoanContext"

const LoanArchiveButton = ({
  loanId,
  onPress,
}: {
  loanId?: string
  onPress: () => void
}) => {
  return (
    <Pressable
      testID={"loan-archive-button"}
      onPress={onPress}
      disabled={!loanId}
      hitSlop={16}
    >
      <Horizontal
        className={"justify-center items-center self-stretch gap-[6px]"}
        style={{ opacity: loanId ? 1 : 0.5 }}
      >
        <ArchiveIcon width={16} height={16} color={BFColors.calmBlue} />
        <H5 className={"text-calm-blue"}>Archive</H5>
      </Horizontal>
    </Pressable>
  )
}

export const LoanSetupScreen = ({
  title,
  subTitle,
  stepData,
  address,
  children,
  nextButtonProps,
  onSubmit,
  backButton,
  hideSetupHeader = false,
  hideNextButton = false,
  bottomBanner,
}: {
  title: string
  subTitle?: string
  stepData?: [number, number]
  address?: string
  children: ReactNode
  nextButtonProps?: Partial<CommonButtonProps>
  onSubmit?: () => Promise<void>
  backButton?: () => void
  hideSetupHeader?: boolean
  hideNextButton?: boolean
  bottomBanner?: ReactNode
}) => {
  const [loading, setLoading] = useState(false)
  const [showArchivePopover, setShowArchivePopover] = useState(false)
  const { disabled, ...buttonProps } = nextButtonProps || {}
  const insets = useSafeAreaInsets()
  const archiveLoanMutation = useArchiveLoanMutation()
  const { loanId } = useLoanContext()

  const handleArchive = () => {
    if (!loanId) return

    archiveLoanMutation.mutate(
      { loanId },
      {
        onSuccess(resp) {
          showSuccessToast({
            title: "Quote Archived",
            autoHide: true,
            visibilityTime: 5000,
            onPress: () => {
              hideToast()
            },
          })
          router.dismissTo("/loans")
        },
      }
    )
  }

  return (
    <BFScreen dark={true} gradientBackground={true} noBackButton={true}>
      <Vertical className={"flex-1 "}>
        <View style={{ height: insets.top }} />
        <Horizontal
          testID={"get-quote-header"}
          className={"w-full justify-between px-4 py-5 items-center z-10"}
        >
          {backButton ? (
            <BFBackButton onBack={backButton} />
          ) : (
            <View className={"h-[23px] w-[30px]"} />
          )}
          <Vertical className={"items-center"}>
            <H5 color={"white"}>Get a Quote</H5>
            {address && <Small color={"white"}>{address}</Small>}
          </Vertical>
          <Pressable
            testID={"close-button"}
            hitSlop={32}
            onPress={() => {
              router.push("/loans")
            }}
          >
            <H6 color={"teal"}>Close</H6>
          </Pressable>
        </Horizontal>
        <View
          className={"flex-1 bg-off-white "}
          style={{ paddingBottom: insets.bottom }}
        >
          <ScrollView className={"flex-1"}>
            {!hideSetupHeader && (
              <SetupHeader
                title={title}
                subtitle={subTitle}
                stepData={stepData}
              />
            )}
            {children}
          </ScrollView>

          {bottomBanner}

          {!hideNextButton && (
            <View className={"p-5 border-t border-light-grey"}>
              <Horizontal className={"gap-3"}>
                {loanId && (
                  <View className="justify-center px-6">
                    <LoanArchiveButton
                      loanId={loanId}
                      onPress={() => {
                        setShowArchivePopover(true)
                      }}
                    />
                  </View>
                )}

                <View className="flex flex-1">
                  <BFButton
                    variant={"secondary"}
                    testID={"next-button"}
                    size={"lg"}
                    title={"Close"}
                    loading={loading}
                    disabled={disabled || loading}
                    {...buttonProps}
                    onPress={() => {
                      setLoading(true)
                      void (async () => {
                        try {
                          await onSubmit?.()
                        } finally {
                          setLoading(false)
                        }
                      })()
                    }}
                  />
                </View>
              </Horizontal>
            </View>
          )}
        </View>
      </Vertical>

      <LoanArchivePopover
        visible={showArchivePopover}
        onClose={() => {
          setShowArchivePopover(false)
        }}
        onArchive={handleArchive}
      />
    </BFScreen>
  )
}
